class CollectionRangeSlider extends HTMLElement {
  static get observedAttributes() {
    return ['min', 'max', 'step', 'value-min', 'value-max', 'tick-count', 'tick-mode'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._silent = false;
    this._dragging = null;
  }

  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.render();
  }

  attributeChangedCallback() {
    if (!this._ready) return;
    this.syncFromAttributes();
  }

  get min() {
    var n = parseFloat(this.getAttribute('min'));
    return isNaN(n) ? 0 : n;
  }

  get max() {
    var n = parseFloat(this.getAttribute('max'));
    return isNaN(n) ? 0 : n;
  }

  get step() {
    var n = parseFloat(this.getAttribute('step'));
    return n > 0 ? n : 1;
  }

  selected() {
    return {
      minSelected: this.start ? parseFloat(this.start.value) : this.min,
      maxSelected: this.end ? parseFloat(this.end.value) : this.max
    };
  }

  setRange(min, max, selectedMin, selectedMax, silent) {
    this._silent = !!silent;
    this.setAttribute('min', String(min));
    this.setAttribute('max', String(max));
    this.setAttribute('value-min', String(selectedMin));
    this.setAttribute('value-max', String(selectedMax));
    this.render();
    this._silent = false;
  }

  parseSelected() {
    var lo = parseFloat(this.getAttribute('value-min'));
    var hi = parseFloat(this.getAttribute('value-max'));
    if (isNaN(lo)) lo = this.min;
    if (isNaN(hi)) hi = this.max;
    lo = Math.min(Math.max(lo, this.min), this.max);
    hi = Math.min(Math.max(hi, this.min), this.max);
    if (lo > hi) lo = hi;
    return { lo: lo, hi: hi };
  }

  integerTicks() {
    return this.getAttribute('tick-mode') === 'integer';
  }

  tickValues() {
    var min = this.min;
    var max = this.max;
    if (this.integerTicks()) {
      var lo = 0;
      var hi = Math.ceil(max);
      if (hi <= lo) hi = lo + 1;
      var values = [];
      var n;
      for (n = lo; n <= hi; n++) values.push(n);
      return values;
    }
    var ticks = parseInt(this.getAttribute('tick-count'), 10) || 5;
    if (ticks < 2) ticks = 2;
    var even = [];
    var i;
    for (i = 0; i < ticks; i++) {
      even.push(min + (max - min) * (i / (ticks - 1)));
    }
    return even;
  }

  // Hosts can set a tickFormatter property (before the element is connected)
  // to label the track in their own units, e.g. converted prices.
  formatTick(value) {
    if (typeof this.tickFormatter === 'function') return this.tickFormatter(value);
    if (this.integerTicks()) return String(Math.round(value));
    var max = this.max;
    if (max >= 1000) {
      if (value >= 1000000) {
        var m = value / 1000000;
        return (Number.isInteger(m) ? m : m.toFixed(1)) + 'm';
      }
      var k = value / 1000;
      return (k >= 10 || Number.isInteger(k) ? Math.round(k) : k.toFixed(1)) + 'k';
    }
    return String(Math.round(value * 10) / 10);
  }

  render() {
    var min = this.min;
    var max = this.max;
    var step = this.step;
    var selected = this.parseSelected();
    var tickValues = this.tickValues();
    var span = max - min;
    if (!(span > 0)) span = 1;

    var markers = tickValues
      .map(function (value) {
        return '<div class="marker" style="left:' + ((value - min) / span) * 100 + '%"></div>';
      })
      .join('');
    var labels = tickValues
      .map(
        function (value) {
          return (
            '<span style="left:' +
            ((value - min) / span) * 100 +
            '%">' +
            this.formatTick(value) +
            '</span>'
          );
        }.bind(this)
      )
      .join('');

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; width: 100%; font-family: 'Montserrat'; }
        .wrap { width: 100%; position: relative; }
        .track { height: 20px; position: relative; background: none; margin-top: 16px; }
        .track::before { content: ''; position: absolute; left: 0; right: 0; top: 9px; height: 2px; background: #e0e0e0; }
        .fill { position: absolute; top: 9px; height: 2px; background: #333; z-index: 1; }
        .markers { position: absolute; left: 0; right: 0; top: 9px; height: 2px; }
        .marker { width: 1px; height: 10px; background: #333333; position: absolute; top: -4px; transform: translateX(-50%); z-index: 2; }
        .marker:first-child, .marker:last-child { width: 0; }
        .range { position: absolute; width: calc(100% + 16px); left: -8px; top: 9px; height: 2px; margin: 0; background: none; border: none; pointer-events: none; -webkit-appearance: none; appearance: none; }
        .range::-webkit-slider-thumb { height: 20px; width: 20px; border-radius: 50%; background: #fff; border: 1px solid #222; pointer-events: auto; -webkit-appearance: none; cursor: pointer; position: relative; z-index: 2; }
        .range::-moz-range-thumb { height: 20px; width: 20px; border-radius: 50%; background: #fff; border: 1px solid #222; pointer-events: auto; cursor: pointer; }
        .labels { position: relative; height: 28px; margin: 4px 0 0; }
        .labels span { position: absolute; transform: translateX(-50%); font-size: 10px; font-weight: 500; top: 10px; white-space: nowrap; color: #6b6b6b; }
        .labels span:first-child { transform: none; }
        .labels span:last-child { transform: translateX(-100%); }
        .inputs { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
        .inputs span { color: #888; font-size: 14px; flex-shrink: 0; }
        .inputs input { font-family: 'Montserrat' !important;text-align: center;  flex: 1; box-sizing: border-box; min-width: 0; height: 36px; padding: 8px 10px; border: 1px solid #ccc; border-radius: 2px; font-size: 14px; }
      </style>
      <div class="wrap">
        <div class="inputs">
          <input type="number" class="num start-num" min="${min}" max="${max}" step="${step}" value="${selected.lo}">
          <span>–</span>
          <input type="number" class="num end-num" min="${min}" max="${max}" step="${step}" value="${selected.hi}">
        </div>
        <div class="track">
          <div class="fill"></div>
          <div class="markers">${markers}</div>
          <input type="range" class="range start" min="${min}" max="${max}" step="${step}" value="${selected.lo}">
          <input type="range" class="range end" min="${min}" max="${max}" step="${step}" value="${selected.hi}">
        </div>
        <div class="labels">${labels}</div>
      </div>
    `;

    this.start = this.shadowRoot.querySelector('.start');
    this.end = this.shadowRoot.querySelector('.end');
    this.startNum = this.shadowRoot.querySelector('.start-num');
    this.endNum = this.shadowRoot.querySelector('.end-num');
    this.fill = this.shadowRoot.querySelector('.fill');
    this.bind();
    this.updateFill();
  }

  bind() {
    var self = this;
    function finishDrag() {
      if (!self._dragging) return;
      self._dragging = null;
      self.emit();
    }
    this.start.addEventListener('input', function () {
      self._dragging = 'start';
      self.onSlide();
    });
    this.end.addEventListener('input', function () {
      self._dragging = 'end';
      self.onSlide();
    });
    this.start.addEventListener('change', finishDrag);
    this.end.addEventListener('change', finishDrag);
    this.start.addEventListener('pointerup', finishDrag);
    this.end.addEventListener('pointerup', finishDrag);
    this.startNum.addEventListener('change', function () {
      self.onNumber('start');
    });
    this.endNum.addEventListener('change', function () {
      self.onNumber('end');
    });
  }

  onSlide() {
    var lo = parseFloat(this.start.value);
    var hi = parseFloat(this.end.value);
    if (lo > hi) {
      if (this._dragging === 'start') hi = lo;
      else lo = hi;
      this.start.value = lo;
      this.end.value = hi;
    }
    this.startNum.value = lo;
    this.endNum.value = hi;
    this.updateFill();
  }

  onNumber(which) {
    var lo = parseFloat(this.startNum.value);
    var hi = parseFloat(this.endNum.value);
    if (isNaN(lo)) lo = this.min;
    if (isNaN(hi)) hi = this.max;
    lo = Math.min(Math.max(lo, this.min), this.max);
    hi = Math.min(Math.max(hi, this.min), this.max);
    if (which === 'start' && lo > hi) lo = hi;
    if (which === 'end' && hi < lo) hi = lo;
    this.start.value = lo;
    this.end.value = hi;
    this.startNum.value = lo;
    this.endNum.value = hi;
    this.updateFill();
    this.emit();
  }

  syncFromAttributes() {
    var selected = this.parseSelected();
    if (!this.start) return;
    this.start.min = this.min;
    this.start.max = this.max;
    this.end.min = this.min;
    this.end.max = this.max;
    this.start.step = this.step;
    this.end.step = this.step;
    this.start.value = selected.lo;
    this.end.value = selected.hi;
    this.startNum.min = this.min;
    this.startNum.max = this.max;
    this.endNum.min = this.min;
    this.endNum.max = this.max;
    this.startNum.value = selected.lo;
    this.endNum.value = selected.hi;
    this.updateFill();
  }

  updateFill() {
    if (!this.fill) return;
    var span = this.max - this.min;
    if (!(span > 0)) {
      this.fill.style.left = '0%';
      this.fill.style.width = '100%';
      return;
    }
    var lo = parseFloat(this.start.value);
    var hi = parseFloat(this.end.value);
    this.fill.style.left = ((lo - this.min) / span) * 100 + '%';
    this.fill.style.width = ((hi - lo) / span) * 100 + '%';
  }

  emit() {
    if (this._silent) return;
    this.dispatchEvent(
      new CustomEvent('valueChange', {
        bubbles: true,
        composed: true,
        detail: this.selected()
      })
    );
  }
}

customElements.define('collection-range-slider', CollectionRangeSlider);
